import { useState, useEffect } from 'react';
import companyService from '../services/CompanyService.js';

const LOGO_CACHE_KEY = 'company_logo_cache';

/** In-memory cache so Header remounts (tab changes) keep the same logo instantly. */
let memoryLogo = { companyId: null, logo: null };

function readSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

function readCachedLogo(companyId) {
  if (!companyId) return null;
  const id = String(companyId);

  if (memoryLogo.companyId === id && memoryLogo.logo) {
    return memoryLogo.logo;
  }

  try {
    const raw = sessionStorage.getItem(LOGO_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    if (cache?.company_id === id && cache?.logo) {
      memoryLogo = { companyId: id, logo: cache.logo };
      return cache.logo;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeCachedLogo(companyId, logo) {
  if (!companyId || !logo) return;
  const id = String(companyId);
  memoryLogo = { companyId: id, logo };
  try {
    sessionStorage.setItem(
      LOGO_CACHE_KEY,
      JSON.stringify({ company_id: id, logo })
    );
  } catch {
    /* ignore */
  }
}

export function clearCompanyLogoCache() {
  memoryLogo = { companyId: null, logo: null };
  try {
    sessionStorage.removeItem(LOGO_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function resolveInitialLogo(user) {
  if (user?.company_logo_path) return user.company_logo_path;
  if (user?.company_id) {
    const cached = readCachedLogo(user.company_id);
    if (cached) return cached;
  }
  // Auth may still be loading — use session so first paint after refresh is stable
  const stored = readSessionUser();
  if (stored?.company_logo_path) return stored.company_logo_path;
  if (stored?.company_id) return readCachedLogo(stored.company_id);
  return null;
}

/**
 * Company logo for Header / Sidebar.
 * Cached in memory + sessionStorage so remounts / tab changes do not flash the default brand.
 */
export function useCompanyLogo(user) {
  const [companyLogo, setCompanyLogo] = useState(() => resolveInitialLogo(user));

  useEffect(() => {
    const companyId = user?.company_id;
    const fromUser = user?.company_logo_path || null;

    if (!companyId) {
      // Keep cached logo during brief auth reload; clear only on real logout
      if (!sessionStorage.getItem('user')) {
        setCompanyLogo(null);
        clearCompanyLogoCache();
      }
      return;
    }

    if (fromUser) {
      setCompanyLogo(fromUser);
      writeCachedLogo(companyId, fromUser);
      return;
    }

    const cached = readCachedLogo(companyId);
    if (cached) {
      setCompanyLogo(cached);
    }

    let cancelled = false;
    companyService
      .getById(companyId)
      .then((response) => {
        const logo = response.data?.logo_path || null;
        if (cancelled) return;
        if (logo) {
          setCompanyLogo(logo);
          writeCachedLogo(companyId, logo);
        } else if (!cached) {
          setCompanyLogo(null);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [user?.company_id, user?.company_logo_path]);

  return companyLogo;
}
